/**
 * AudioManager - Manages WebRTC audio connections via PartyTracks and Cloudflare Realtime SFU
 *
 * This class handles:
 * - Creating and managing WebRTC connections via PartyTracks
 * - Publishing local audio tracks (for stage users)
 * - Subscribing to remote audio tracks (for listeners)
 * - Mute/unmute functionality
 * - Audio playback management
 *
 * PartyTracks provides:
 * - Automatic reconnection on network changes
 * - Resilient track handling with device fallbacks
 * - Batched track operations
 * - RxJS-based reactive state management
 */
import { PartyTracks } from 'partytracks/client';
import { getMic } from 'partytracks/client';
import { BehaviorSubject } from 'rxjs';
import { Observable } from '@towns-protocol/sdk';
// ============================================================================
// AudioManager Class
// ============================================================================
export class LiveAudioManager {
    partyTracks;
    audioElements = new Map();
    subscriptions = new Map();
    micSubscriptions = [];
    mic = null;
    delegate;
    localSpeakingMonitor = null;
    preferredOutputDeviceId = null;
    state$;
    spaceId;
    channelId;
    constructor(params) {
        // Throw error if in test environment - tests should use NoOpAudioManager instead
        if (process.env.NODE_ENV === 'test' || typeof navigator === 'undefined') {
            throw new Error('LiveAudioManager cannot be instantiated in test environment. ' +
                'Use NoOpAudioManager or provide an audioManagerFactory in LiveClientOptions.');
        }
        this.delegate = params.delegate;
        this.spaceId = params.spaceId;
        this.channelId = params.channelId;
        console.log('[AudioManager] constructor', params.apiBase);
        // Initialize PartyTracks pointing to our live-worker with auth params
        this.partyTracks = new PartyTracks({
            prefix: `${params.apiBase}/partytracks`,
            apiExtraParams: `spaceId=${encodeURIComponent(params.spaceId)}&token=${encodeURIComponent(params.partytracksToken)}`,
        });
        // Initialize state
        this.state$ = new Observable({
            status: 'idle',
            subscribedTracks: new Map(),
            localMuted: false,
            remoteMuteStates: new Map(),
            speakingUsers: new Set(),
            mic: {
                isRequested: false,
                isBroadcasting: false,
                isSourceEnabled: false,
            },
        });
    }
    // ========================================================================
    // Public Methods
    // ========================================================================
    /**
     * Initialize audio session and publish microphone
     * This will request microphone access and publish the audio track
     */
    async initMic() {
        if (this.state$.value.status === 'connected') {
            console.log('[AudioManager] Already connected');
            return;
        }
        console.log('[AudioManager] initSession');
        this.updateState({ status: 'initializing', error: undefined });
        this.updateMicState({ isRequested: true });
        try {
            // Get microphone with resilient handling using PartyTracks
            this.mic = getMic();
            this.micSubscriptions.push(this.mic.error$.subscribe((error) => {
                console.error('[AudioManager] Microphone error:', error);
                this.updateMicState({ error });
            }));
            this.micSubscriptions.push(this.mic.permissionState$.subscribe((permissionState) => {
                console.log('[AudioManager] Microphone permission state:', permissionState);
                this.updateMicState({ permissionState });
            }));
            this.micSubscriptions.push(this.mic.isBroadcasting$.subscribe((isBroadcasting) => {
                console.log('[AudioManager] Microphone is broadcasting:', isBroadcasting);
                this.updateMicState({ isBroadcasting });
            }));
            this.micSubscriptions.push(this.mic.isSourceEnabled$.subscribe((isSourceEnabled) => {
                console.log('[AudioManager] Microphone is source enabled:', isSourceEnabled);
                this.updateMicState({ isSourceEnabled });
            }));
            this.micSubscriptions.push(this.mic.activeDevice$.subscribe((activeDevice) => {
                console.log('[AudioManager] Microphone active device:', activeDevice);
                this.updateMicState({ activeDevice });
            }));
            this.micSubscriptions.push(this.mic.devices$.subscribe((devices) => {
                console.log('[AudioManager] Microphone devices:', devices);
                this.updateMicState({ devices });
            }));
            // Monitor local mic for speaking detection
            this.micSubscriptions.push(this.mic.localMonitorTrack$.subscribe((track) => {
                this.setupLocalSpeakingDetection(track);
            }));
            // Wait for first successful publish
            this.micSubscriptions.push(this.partyTracks.push(this.mic.broadcastTrack$).subscribe((metadata) => {
                console.log('[AudioManager] broadcast track metadata changed:', metadata);
                const trackId = `${metadata.sessionId}:${metadata.trackName}`;
                if (trackId !== this.state$.value.publishedTrackId) {
                    console.log('[AudioManager] Publishing mic track metadata:', trackId);
                    this.publishTrack(metadata);
                }
            }));
            this.mic.enableSource();
            if (this.state$.value.localMuted) {
                this.mic.stopBroadcasting();
            }
            else {
                this.mic.startBroadcasting();
            }
        }
        catch (error) {
            this.handleError(error);
            throw error;
        }
    }
    /**
     * Stop the microphone (when leaving stage but staying in channel)
     * Resets status to 'idle' so initMic() can be called again on rejoin
     */
    async stopMic() {
        this.updateState({ status: 'idle', publishedTrackId: undefined });
        this.updateMicState({ isRequested: false, isBroadcasting: false, isSourceEnabled: false });
        this.destroyMic();
    }
    /**
     * Subscribe to remote audio tracks
     * Call this after receiving audio_tracks_published messages or from channel state
     */
    async subscribeTracks(trackInfos) {
        console.log(`[AudioManager] Subscribing to tracks: ${trackInfos.length}`);
        for (const trackInfo of trackInfos) {
            // Skip if already subscribed
            if (this.state$.value.subscribedTracks.has(trackInfo.trackId)) {
                continue;
            }
            // Skip our own track (by trackId or by userId)
            if (trackInfo.trackId === this.state$.value.publishedTrackId) {
                console.log(`[AudioManager] Skipping own track (by trackId): ${trackInfo.trackId}`);
                continue;
            }
            // skip if it's our user id
            if (trackInfo.userId === this.delegate?.userId) {
                console.log(`[AudioManager] Skipping own track (by userId): ${trackInfo.userId}`);
                continue;
            }
            // trackId format: "sessionId:trackName"
            const [sessionId, trackName] = trackInfo.trackId.split(':');
            if (!sessionId || !trackName) {
                console.warn(`[AudioManager] Invalid track data: sessionId=${sessionId}, trackName=${trackName}`);
                continue;
            }
            console.log(`[AudioManager] Subscribing to track: ${trackInfo.trackId} (session=${sessionId}, name=${trackName})`);
            // Create track data observable for PartyTracks
            // Must include location: 'remote' to indicate this is a remote track to subscribe to
            const trackData$ = new BehaviorSubject({
                location: 'remote',
                sessionId,
                trackName,
            });
            // Pull track from SFU
            const remoteTrack$ = this.partyTracks.pull(trackData$);
            const subscription = remoteTrack$.subscribe((track) => {
                console.log('[AudioManager] Attaching audio element to track: ', trackInfo.trackId);
                this.attachAudioElement(trackInfo.userId, trackInfo.trackId, track);
            });
            this.subscriptions.set(trackInfo.trackId, subscription);
            // Update state
            this.state$.set((prev) => ({
                ...prev,
                subscribedTracks: new Map(prev.subscribedTracks).set(trackInfo.trackId, {
                    trackId: trackInfo.trackId,
                    userId: trackInfo.userId,
                    sessionId,
                    trackName,
                }),
            }));
        }
    }
    /**
     * Attach an audio element to play a remote track, deprecated in favor of audio sink
     */
    attachAudioElement(userId, trackId, track) {
        console.log(`[AudioManager] Attaching audio element to track: ${trackId}`);
        // Remove existing element if any
        const stub = this.audioElements.get(trackId);
        if (stub) {
            stub.cleanup();
        }
        // Create new audio element
        const audioElement = document.createElement('audio');
        const stream = new MediaStream([track]);
        audioElement.srcObject = stream;
        audioElement.autoplay = true;
        audioElement.setAttribute('playsinline', 'true');
        // is talking monitor
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        // Still connect to audio output for playback
        // (or use createAudioSink separately)
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        let wasSpeaking = false;
        const checkSpeaking = () => {
            analyser.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
            // Don't show as speaking if remote user is muted
            const isMuted = this.state$.value.remoteMuteStates.get(userId) ?? false;
            const isSpeaking = !isMuted && average > 20;
            if (isSpeaking !== wasSpeaking) {
                wasSpeaking = isSpeaking;
                this.updateSpeakingState(userId, isSpeaking);
            }
        };
        const interval = setInterval(checkSpeaking, 300);
        // Store for cleanup
        this.audioElements.set(trackId, {
            element: audioElement,
            cleanup: () => {
                // clean up the audio context
                audioContext.close();
                source.disconnect();
                analyser.disconnect();
                // clean up the audio element
                audioElement.pause();
                audioElement.srcObject = null;
                audioElement.remove();
                // stop the is talking monitor
                clearInterval(interval);
            },
        });
        // Apply preferred output device if set
        if (this.preferredOutputDeviceId) {
            const el = audioElement;
            if (el.setSinkId) {
                el.setSinkId(this.preferredOutputDeviceId).catch((err) => {
                    console.error(`[AudioManager] Failed to set sink for new track ${trackId}:`, err);
                });
            }
        }
        // Start playing (this is how PartyTracks does it, claude wanted to just call play... that works too)
        audioElement.addEventListener('canplay', () => audioElement.play(), {
            once: true,
        });
        return stream;
    }
    /** unsubscribe from multiple tracks */
    unsubscribeTracks(trackIds) {
        for (const trackId of trackIds) {
            this.unsubscribeTrack(trackId);
        }
    }
    /**
     * Unsubscribe from a specific track
     */
    unsubscribeTrack(trackId) {
        const subscribedTracks = new Map(this.state$.value.subscribedTracks);
        console.log(`[AudioManager] Unsubscribing from track: ${trackId}`);
        if (subscribedTracks.has(trackId)) {
            try {
                this.audioElements.get(trackId)?.cleanup();
                this.subscriptions.get(trackId)?.unsubscribe();
            }
            catch (error) {
                console.error(`[AudioManager] Failed to unsubscribe from track: ${trackId}`, error);
            }
            finally {
                this.audioElements.delete(trackId);
                this.subscriptions.delete(trackId);
                subscribedTracks.delete(trackId);
                this.updateState({ subscribedTracks });
                console.log(`[AudioManager] Unsubscribed from track: ${trackId}`);
            }
        }
        // PartyTracks handles cleanup when observable completes
    }
    /**
     * Set local mute state
     */
    setMuted(muted) {
        // Disable/enable the current mic track directly
        if (muted) {
            this.mic?.stopBroadcasting();
        }
        else {
            this.mic?.startBroadcasting();
        }
        this.updateState({ localMuted: muted });
        console.log(`[AudioManager] Local mute set to ${muted}`);
    }
    /**
     * Set preferred microphone device
     */
    setPreferredDevice(device) {
        if (this.mic) {
            this.mic.setPreferredDevice(device);
            console.log(`[AudioManager] Preferred device set to ${device.label}`);
        }
        else {
            console.warn('[AudioManager] Cannot set preferred device: mic not initialized');
        }
    }
    /**
     * Set preferred output (speaker) device
     * Uses setSinkId API to route audio to the selected device
     */
    async setOutputDevice(deviceId) {
        this.preferredOutputDeviceId = deviceId;
        console.log(`[AudioManager] Setting output device to ${deviceId}`);
        // Apply to all existing audio elements
        const promises = [];
        for (const [trackId, { element }] of this.audioElements) {
            // setSinkId is not in all TS typings, cast to any
            const audioElement = element;
            if (audioElement.setSinkId) {
                promises.push(audioElement.setSinkId(deviceId).catch((err) => {
                    console.error(`[AudioManager] Failed to set sink for track ${trackId}:`, err);
                }));
            }
        }
        await Promise.all(promises);
        console.log(`[AudioManager] Output device set to ${deviceId} for ${this.audioElements.size} elements`);
    }
    /**
     * Toggle mute state
     */
    toggleMute() {
        this.setMuted(!this.state$.value.localMuted);
    }
    /**
     * Check if a remote user is muted
     */
    isRemoteMuted(userId) {
        return this.state$.value.remoteMuteStates.get(userId) ?? false;
    }
    destroyMic() {
        // Clean up local speaking monitor
        if (this.localSpeakingMonitor) {
            this.localSpeakingMonitor.cleanup();
            this.localSpeakingMonitor = null;
        }
        // Clear speaking state for local user
        if (this.delegate?.userId) {
            this.updateSpeakingState(this.delegate.userId, false);
        }
        this.micSubscriptions.forEach((sub) => sub.unsubscribe());
        this.micSubscriptions = [];
        this.mic?.stopBroadcasting();
        this.mic?.disableSource();
        this.mic = null;
    }
    /**
     * Clean up all audio resources
     */
    async destroy() {
        this.state$.dispose();
        console.log('[AudioManager] Destroying audio manager');
        // Unsubscribe mic subscription
        this.destroyMic();
        // Unsubscribe all RxJS subscriptions
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions.clear();
        // Clean up audio elements
        this.audioElements.forEach((stub) => {
            stub.cleanup();
        });
        this.audioElements.clear();
        this.delegate = undefined;
    }
    // ========================================================================
    // Private Methods
    // ========================================================================
    publishTrack(metadata) {
        if (!metadata.sessionId || !metadata.trackName) {
            console.error('[AudioManager] Invalid track metadata: sessionId or trackName is missing');
            return;
        }
        const trackId = `${metadata.sessionId}:${metadata.trackName}`;
        console.log(`[AudioManager] publishing track: ${trackId}`);
        this.updateState({
            status: 'connected',
            sessionId: metadata.sessionId,
            publishedTrackId: trackId,
        });
        this.delegate
            ?.publishAudioTrack(trackId)
            .then((resp) => {
            console.log(`[AudioManager] Published track: ${trackId}`, resp);
        })
            .catch((err) => {
            console.error(`[AudioManager] Failed to publish track: ${trackId}`, err);
            this.handleError(err);
        });
    }
    handleError(error) {
        console.error('[AudioManager] Error:', error);
        let code = 'INTERNAL_ERROR';
        if (error instanceof Error) {
            if (error.name === 'NotAllowedError') {
                code = 'MICROPHONE_PERMISSION_DENIED';
            }
        }
        this.updateState({
            status: 'error',
            error: {
                type: 'audio_error_response',
                code,
                message: error instanceof Error ? error.message : 'Unknown error',
                timestamp: Date.now(),
                nonce: -1,
            },
        });
    }
    /**
     * Set up speaking detection for the local microphone track
     */
    setupLocalSpeakingDetection(track) {
        const userId = this.delegate?.userId;
        if (!userId) {
            console.warn('[AudioManager] Cannot set up local speaking detection: no userId');
            return;
        }
        // Clean up existing monitor if any
        if (this.localSpeakingMonitor) {
            this.localSpeakingMonitor.cleanup();
        }
        // Create audio context and analyzer for the local mic track
        const audioContext = new AudioContext();
        const stream = new MediaStream([track]);
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        let wasSpeaking = false;
        const checkSpeaking = () => {
            analyser.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
            // Don't show as speaking if muted
            const isMuted = this.state$.value.localMuted;
            const isSpeaking = !isMuted && average > 20;
            if (isSpeaking !== wasSpeaking) {
                wasSpeaking = isSpeaking;
                this.updateSpeakingState(userId, isSpeaking);
            }
        };
        const interval = setInterval(checkSpeaking, 300);
        // Store cleanup function
        this.localSpeakingMonitor = {
            cleanup: () => {
                clearInterval(interval);
                source.disconnect();
                analyser.disconnect();
                audioContext.close();
            },
        };
    }
    updateState(updates) {
        this.state$.set((prev) => ({
            ...prev,
            ...updates,
        }));
    }
    updateMicState(updates) {
        this.state$.set((prev) => ({
            ...prev,
            mic: {
                ...prev.mic,
                ...updates,
            },
        }));
    }
    updateSpeakingState(userId, isSpeaking) {
        this.state$.set((prev) => {
            const speakingUsers = new Set(prev.speakingUsers);
            if (isSpeaking) {
                speakingUsers.add(userId);
            }
            else {
                speakingUsers.delete(userId);
            }
            return { ...prev, speakingUsers };
        });
    }
}
// ============================================================================
// No-Op Audio Manager (for testing)
// ============================================================================
/**
 * No-op implementation of ILiveAudioManager for testing.
 * This allows tests to run without WebRTC/browser APIs.
 */
export class NoOpAudioManager {
    state$;
    spaceId;
    channelId;
    delegate;
    constructor(params) {
        this.delegate = params.delegate;
        this.spaceId = params.spaceId;
        this.channelId = params.channelId;
        // Initialize with idle state
        this.state$ = new Observable({
            status: 'idle',
            subscribedTracks: new Map(),
            localMuted: false,
            remoteMuteStates: new Map(),
            speakingUsers: new Set(),
            mic: {
                isRequested: false,
                isBroadcasting: false,
                isSourceEnabled: false,
            },
        });
    }
    async initMic() {
        const sessionId = crypto.randomUUID();
        const trackName = `test-track-${crypto.randomUUID()}`;
        const trackId = `${sessionId}:${trackName}`;
        const response = await this.delegate.publishAudioTrack(trackId);
        console.log(`[NoOpAudioManager] Published track: ${trackId}`, response);
        // No-op: just update state to connected
        this.state$.set(() => ({
            status: 'connected',
            subscribedTracks: new Map(),
            localMuted: false,
            remoteMuteStates: new Map(),
            speakingUsers: new Set(),
            mic: {
                isRequested: false,
                isBroadcasting: true,
                isSourceEnabled: true,
            },
        }));
    }
    async stopMic() {
        this.state$.set((prev) => ({
            ...prev,
            status: 'idle',
            publishedTrackId: undefined,
            mic: { isRequested: false, isBroadcasting: false, isSourceEnabled: false },
        }));
    }
    async subscribeTracks(tracks) {
        const subscribedTracks = new Map(this.state$.value.subscribedTracks);
        for (const trackInfo of tracks) {
            subscribedTracks.set(trackInfo.trackId, {
                trackId: trackInfo.trackId,
                userId: trackInfo.userId,
                sessionId: trackInfo.trackId.split(':')[0],
                trackName: trackInfo.trackId.split(':')[1],
            });
        }
        this.state$.set((prev) => ({
            ...prev,
            subscribedTracks,
        }));
    }
    unsubscribeTracks(trackIds) {
        const subscribedTracks = new Map(this.state$.value.subscribedTracks);
        for (const trackId of trackIds) {
            subscribedTracks.delete(trackId);
        }
        this.state$.set((prev) => ({
            ...prev,
            subscribedTracks,
        }));
    }
    setMuted(_muted) {
        // No-op
    }
    setPreferredDevice(_device) {
        // No-op
    }
    async setOutputDevice(_deviceId) {
        // No-op
    }
    startBroadcasting() {
        // No-op
    }
    async destroy() {
        // No-op
    }
}
//# sourceMappingURL=LiveAudioManager.js.map